import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock, Info } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { SectionHead } from "@/components/SectionHead";
import { CouponBox } from "@/components/CouponBox";
import { Reveal } from "@/lib/motion";
import { imgSrc } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "", notes: "" };
const inputCls = "w-full bg-white border border-forest/15 rounded-xl px-4 py-3 text-sm text-ink placeholder:text-mutedgreen/60 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-colors";

const F = ({ label, name, form, set, type = "text", required = true, className = "", ...rest }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-1.5">{label}</span>
    <input data-testid={`checkout-${name}`} type={type} required={required} value={form[name]} onChange={(e) => set(name, e.target.value)} className={inputCls} {...rest} />
  </label>
);

export default function Checkout() {
  const { cart, refresh } = useCart();
  const settings = useSettings();
  const { user } = useCustomerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
      address: f.address || user.address || "",
      city: f.city || user.city || "",
      state: f.state || user.state || "",
      pincode: f.pincode || user.pincode || "",
    }));
  }, [user]);

  const place = async (e) => {
    e.preventDefault();
    if (settings.coming_soon) return toast.error(settings.checkout_message);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { session_id: localStorage.getItem("nb_session"), customer: form });
      localStorage.setItem("nb_last_order", JSON.stringify({ order_number: data.order_number, email: data.customer.email }));
      await refresh();
      navigate(`/order/${data.order_number}?email=${encodeURIComponent(data.customer.email)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="container-nb py-32 text-center">
        <p className="font-serif text-3xl text-forest">Your basket is empty.</p>
        <Link to="/collection" className="inline-flex items-center gap-2 mt-6 text-xs font-bold uppercase tracking-[0.18em] text-forest link-underline">Explore the collection <ArrowRight size={13} /></Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="py-16 sm:py-24">
      <div className="container-nb">
        <Link to="/cart" data-testid="checkout-back-link" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-mutedgreen hover:text-forest transition-colors mb-6"><ArrowLeft size={13} /> Back to basket</Link>
        <SectionHead center={false} eyebrow="Checkout" title="Where shall we send nature's finest?" className="mb-10" />

        <form onSubmit={place} className="grid lg:grid-cols-[1.6fr_1fr] gap-10 items-start">
          <Reveal>
            <div className="bg-white border border-forest/10 rounded-3xl p-6 sm:p-8 space-y-5">
              <h3 className="font-serif text-2xl text-forest">Delivery details</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Full name" name="name" form={form} set={set} />
                <F label="Phone" name="phone" form={form} set={set} type="tel" inputMode="numeric" placeholder="10-digit mobile" />
              </div>
              <F label="Email" name="email" form={form} set={set} type="email" />
              <F label="Address" name="address" form={form} set={set} placeholder="House / flat, street, landmark" />
              <div className="grid sm:grid-cols-3 gap-4">
                <F label="City" name="city" form={form} set={set} />
                <F label="State" name="state" form={form} set={set} />
                <F label="Pincode" name="pincode" form={form} set={set} inputMode="numeric" maxLength={6} placeholder="6 digits" />
              </div>
              <label className="block">
                <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-1.5">Order notes (optional)</span>
                <textarea data-testid="checkout-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} placeholder="Delivery instructions, gift message…" />
              </label>
              <div data-testid="checkout-payment-notice" className="flex gap-3 bg-[#FBF6E8] border border-gold/30 rounded-2xl p-4 text-sm text-forest">
                <Info size={16} className="text-gold shrink-0 mt-0.5" />
                <p><span className="font-semibold">Payment:</span> online payment is being set up. Place your order now and our team will confirm it and share payment options (UPI / bank transfer / pay on delivery) by phone or email.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="checkout-summary" className="bg-forestdeep grain relative rounded-3xl p-7 text-cream lg:sticky lg:top-28">
              <h3 className="font-serif text-2xl">Your order</h3>
              <ul className="mt-5 space-y-3 max-h-64 overflow-y-auto pr-1">
                {cart.items.map((i) => (
                  <li key={i.key} className="flex items-center gap-3 text-sm">
                    <img src={imgSrc(i.product.image)} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
                    <span className="flex-1 min-w-0 truncate">{i.product.name} <span className="text-cream/50">{i.variant ? `· ${i.variant.label} ` : ""}× {i.qty}</span></span>
                    <span className="shrink-0">₹{i.line_total}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5"><CouponBox /></div>
              <div className="mt-5 space-y-2.5 text-sm">
                <div className="flex justify-between text-cream/70"><span>Subtotal</span><span>₹{cart.subtotal}</span></div>
                {cart.discount > 0 && <div className="flex justify-between text-goldlight"><span>Discount</span><span>− ₹{cart.discount}</span></div>}
                <div className="flex justify-between text-cream/70"><span>Shipping</span><span className={cart.shipping > 0 ? "" : "text-goldlight"}>{cart.shipping > 0 ? `₹${cart.shipping}` : "Free"}</span></div>
                <div className="border-t border-cream/15 pt-3 flex justify-between items-baseline"><span className="font-semibold">Total</span><span data-testid="checkout-total" className="font-serif text-3xl">₹{cart.total}</span></div>
              </div>
              <button data-testid="place-order-button" type="submit" disabled={busy} className="mt-6 w-full flex items-center justify-center gap-2 bg-gold hover:bg-goldlight text-white text-xs font-bold uppercase tracking-[0.18em] py-4 rounded-full transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />} Place Order
              </button>
              <p className="text-[10px] text-cream/50 mt-4 text-center">By placing an order you agree to our <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy-policy" className="underline">Privacy Policy</Link>.</p>
            </div>
          </Reveal>
        </form>
      </div>
    </div>
  );
}
